// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Menu  (route /menu) — the catch, redesigned as a mobile-first ordering screen
// Order on screen: delivery strip → search → category chips → (real) welcome-coupon banner → the catch →
// sticky cart bar. Everything shown is backed by real data or behaviour: items come from data/seafood.ts,
// search/filter are client-side (the site search deep-links here with ?q=<catch>), EVERY Add / Edit button opens the pack-size sheet (500 g / 1 kg, components/menu/PackSheet)
// which writes to the site cart (hooks/useCart), the delivery strip shows the
// customer's saved default address, and the only promotion is their own redeemable welcome coupon.
// Deliberately NOT here (nothing in the system supports it): wallet balance, "N-minute" delivery promises,
// batch countdowns, chef videos, reviews, licence/ISO badges, discount/strike-through prices, net-weight or glaze
// claims. Parts: components/menu/{DeliveryBar,CouponBanner,ProductCard,PackSheet,CartBar}.
// ─────────────────────────────────────────────────────────────────────────────
import PromoBanners from "../components/PromoBanners";
import StorefrontNotice from "../components/StorefrontNotice";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Fish, LayoutGrid, Search, Shell, Sparkles, X } from "lucide-react";
import PageTransition from "../components/PageTransition";
import Breadcrumbs from "../components/Breadcrumbs";
import CartBar from "../components/menu/CartBar";
import CouponBanner from "../components/menu/CouponBanner";
import DeliveryBar from "../components/menu/DeliveryBar";
import PackSheet from "../components/menu/PackSheet";
import ProductCard from "../components/menu/ProductCard";
import useDocumentTitle from "../hooks/useDocumentTitle";
import usePackSheet from "../hooks/usePackSheet";
import { seafoodItems, type SeafoodCategory } from "../data/seafood";

type Filter = SeafoodCategory | "All";

const CHIPS: { value: Filter; label: string; icon: typeof Fish }[] = [
  { value: "All", label: "All", icon: LayoutGrid },
  { value: "Fish", label: "Fish", icon: Fish },
  { value: "Shellfish", label: "Shellfish", icon: Shell },
  { value: "Premium", label: "Premium", icon: Sparkles },
];

const countIn = (filter: Filter) =>
  filter === "All" ? seafoodItems.length : seafoodItems.filter((item) => item.category === filter).length;

export default function Menu() {
  useDocumentTitle("The Catch");
  const sheet = usePackSheet();
  const [filter, setFilter] = useState<Filter>("All");
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") ?? "");

  // The site search links here with ?q=<catch>; that must also work when already on /menu (same route, no remount).
  const urlQuery = params.get("q");
  useEffect(() => {
    if (urlQuery !== null) setQuery(urlQuery);
  }, [urlQuery]);

  const items = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return seafoodItems.filter(
      (item) =>
        (filter === "All" || item.category === filter) &&
        (needle === "" ||
          `${item.name} ${item.description} ${item.tags.join(" ")} ${item.category}`.toLowerCase().includes(needle)),
    );
  }, [filter, query]);

  const filtered = filter !== "All" || query.trim() !== "";

  return (
    <PageTransition>
      <div className="bg-foam-50 pb-28 pt-28 dark:bg-ocean-950 sm:pt-32">
        <Breadcrumbs />

        <main className="mx-auto flex max-w-5xl flex-col gap-4 px-4 pt-2">
          <header>
            <h1 className="font-display text-3xl font-bold text-ocean-950 dark:text-foam-50 sm:text-4xl">The Catch</h1>
            <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">
              Fresh seafood from India&apos;s coasts, priced per kg. {seafoodItems.length} catches on the menu; availability depends on the day's market.
            </p>
          </header>

          <StorefrontNotice />

          <PromoBanners placement="menu" />

          <DeliveryBar />

          <p className="-mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">
            Rather browse by type? See <Link to="/categories" className="font-semibold text-teal-700 underline underline-offset-2 dark:text-teal-300">Categories</Link>.
          </p>

          <div role="search" className="relative">
            <label htmlFor="menu-search" className="sr-only">Search the catch</label>
            <Search className="pointer-events-none absolute left-4 top-3.5 h-5 w-5 text-ocean-800/70 dark:text-foam-100/70" aria-hidden="true" />
            <input
              id="menu-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search “Pomfret”, “Prawns”, “Crab”…"
              autoComplete="off"
              className="h-12 w-full rounded-2xl bg-white pl-12 pr-12 text-base text-ocean-950 shadow-sm outline-none ring-1 ring-ocean-900/10 placeholder:text-ocean-800/50 focus:ring-2 focus:ring-teal-600 dark:bg-ocean-900 dark:text-foam-50 dark:ring-white/10"
            />
            {query && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setQuery("")}
                className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full hover:bg-ocean-700/10"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
          </div>

          <div role="group" aria-label="Filter by category" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
            {CHIPS.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => setFilter(value)}
                className={`inline-flex min-h-11 shrink-0 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 ${
                  filter === value ? "bg-ocean-800 text-white" : "bg-white text-ocean-900 ring-1 ring-ocean-900/10 hover:bg-ocean-700/5 dark:bg-ocean-900 dark:text-foam-100 dark:ring-white/10"
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
                <span className={`rounded-full px-2 text-xs ${filter === value ? "bg-white/20" : "bg-ocean-700/10 dark:bg-white/10"}`}>{countIn(value)}</span>
              </button>
            ))}
          </div>

          <CouponBanner />

          <p role="status" className="text-sm text-ocean-800/80 dark:text-foam-100/75">
            {filtered ? `Showing ${items.length} of ${seafoodItems.length} catches` : `All ${seafoodItems.length} catches`}
          </p>

          {items.length === 0 ? (
            <div className="rounded-2xl bg-white p-8 text-center shadow-sm dark:bg-ocean-900">
              <p className="font-display text-lg font-bold">Nothing matches that</p>
              <p className="mt-1 text-sm text-ocean-800/80 dark:text-foam-100/75">Try another name, or look at everything.</p>
              <button
                type="button"
                onClick={() => {
                  setQuery("");
                  setFilter("All");
                }}
                className="mt-4 min-h-11 rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700"
              >
                Show all catches
              </button>
            </div>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {items.map((item) => (
                <ProductCard key={item.id} {...sheet.cardProps(item)} />
              ))}
            </ul>
          )}

          <p className="mt-2 text-center text-xs text-ocean-800/70 dark:text-foam-100/65">
            Species photography courtesy of Wikimedia Commons contributors (CC BY / CC BY-SA). Hover a photo for credit.
          </p>

          <section className="mt-6 rounded-2xl bg-foam-100 p-8 text-center dark:bg-ocean-900">
            <h2 className="font-display text-xl font-bold text-ocean-950 dark:text-foam-50">Looking for something specific?</h2>
            <p className="mt-2 text-sm text-ocean-800/80 dark:text-foam-100/75">
              We source seasonal and specialty catches on request. Reach out and we&apos;ll find it.
            </p>
            <Link
              to="/contact"
              className="mt-4 inline-flex min-h-11 items-center rounded-full bg-ocean-800 px-6 text-sm font-bold text-white hover:bg-ocean-700"
            >
              Get in touch
            </Link>
          </section>
        </main>

        <PackSheet {...sheet.sheetProps} />
        <CartBar />
      </div>
    </PageTransition>
  );
}
